import React, { ReactNode } from "react";
import { Row, Col, Form, Button } from "react-bootstrap";
import { X as XIcon, PlusCircle } from "react-bootstrap-icons";
import Branding from "../../config/Branding";
import FilterConditionRow from "./FilterConditionRow";

type FilterGroupCardProps = {
    group: FilterGroup;
    groupIndex: number;
    topLevelOperator: "AND" | "OR";
    availableFilters: Array<DropdownFilter>;
    customerConfig: CustomerConfig;
    onUpdate: (group: FilterGroup) => void;
    onRemove: () => void;
};

/**
 * A card representing one filter group (a set of conditions combined with AND or OR).
 * Multiple groups are always ANDed together at the top level.
 */
export default class FilterGroupCard extends React.Component<FilterGroupCardProps> {
    private handleOperatorChange = (operator: "AND" | "OR"): void => {
        this.props.onUpdate({ ...this.props.group, operator });
    };

    private handleConditionUpdate = (index: number, condition: FilterCondition): void => {
        const conditions = [...this.props.group.conditions];
        conditions[index] = condition;
        this.props.onUpdate({ ...this.props.group, conditions });
    };

    private handleConditionRemove = (index: number): void => {
        const conditions = this.props.group.conditions.filter((_, i) => i !== index);
        this.props.onUpdate({ ...this.props.group, conditions });
    };

    private handleAddCondition = (): void => {
        const defaultTopic = this.props.availableFilters[0]?.qsParam || "";
        const newCondition: FilterCondition = { topic: defaultTopic, values: [] };
        this.props.onUpdate({
            ...this.props.group,
            conditions: [...this.props.group.conditions, newCondition]
        });
    };

    public render(): ReactNode {
        const { group, groupIndex, topLevelOperator, availableFilters, customerConfig } = this.props;

        return (
            <div style={{
                border: "1px solid #3a3a3a",
                borderRadius: 6,
                marginBottom: 10,
                backgroundColor: "rgba(255,255,255,0.03)"
            }}>
                {/* Group header: operator + remove */}
                <div style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    padding: "8px 10px",
                    borderBottom: "1px solid #2a2a2a"
                }}>
                    <span style={{ color: "#aaa", fontSize: 12, flexShrink: 0 }}>
                        {groupIndex === 0 ? "Where" : topLevelOperator === "OR" ? "Or where" : "And where"}
                    </span>
                    <Form.Control
                        as="select"
                        size="sm"
                        value={group.operator}
                        style={{
                            width: "auto",
                            flexShrink: 0,
                            color: Branding.white,
                            backgroundColor: "#222",
                            borderColor: "#555",
                            fontSize: 12
                        }}
                        onChange={(e: any) => this.handleOperatorChange(e.target.value as "AND" | "OR")}
                        title="Combine conditions within this group"
                    >
                        <option value="AND">all of these match</option>
                        <option value="OR">any of these match</option>
                    </Form.Control>
                    <span style={{ flex: 1 }} />
                    <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={this.props.onRemove}
                        style={{ padding: "2px 7px", lineHeight: 1, flexShrink: 0 }}
                        title="Remove group"
                    >
                        <XIcon size={13} />
                    </Button>
                </div>

                {/* Conditions */}
                <div style={{ padding: "8px 0 4px 10px" }}>
                    {group.conditions.length === 0 && (
                        <div style={{ color: "#666", fontSize: 12, fontStyle: "italic", marginBottom: 8 }}>
                            No conditions yet — add one below.
                        </div>
                    )}
                    {group.conditions.map((condition, i) => (
                        <FilterConditionRow
                            key={`cond-${groupIndex}-${i}`}
                            condition={condition}
                            conditionIndex={i}
                            availableFilters={availableFilters}
                            customerConfig={customerConfig}
                            onUpdate={(c) => this.handleConditionUpdate(i, c)}
                            onRemove={() => this.handleConditionRemove(i)}
                        />
                    ))}

                    {/* Add condition */}
                    <Row>
                        <Col>
                            <Button
                                variant="link"
                                size="sm"
                                onClick={this.handleAddCondition}
                                style={{ color: Branding.twentyfouriLime, padding: "2px 0", fontSize: 12 }}
                            >
                                <PlusCircle size={12} style={{ marginRight: 4 }} />
                                Add Condition
                            </Button>
                        </Col>
                    </Row>
                </div>
            </div>
        );
    }
}

